import { Injectable } from "@angular/core";
import { EMPTY_VALS } from "../../constant/validation";
import { SortOrder } from "../../constant/common-table";
import { TableCell, TableRow } from "../../models/common-table";

export interface SortComputationResult {
  currentOrder: SortOrder;
  newOrder: SortOrder;
  sortedRows?: TableRow[];
}

@Injectable({
  providedIn: "root",
})
export class CommonTableSortService {
  public computeSortResult(
    columnTitle: string,
    currentSortOrders: { [colName: string]: SortOrder },
    sortWithApi: boolean,
    rows: TableRow[],
    columnIndex: number,
    customGrandTotal?: TableRow,
  ): SortComputationResult {
    const currentOrder: SortOrder = currentSortOrders[columnTitle] || "ASC";
    const newOrder: SortOrder = this.getNextSortOrder(currentOrder);

    if (sortWithApi) {
      return { currentOrder, newOrder };
    }

    const hasGrandTotal: boolean =
      customGrandTotal !== undefined || rows[rows.length - 1]?.some((cell) => cell?.data?.isTotal);

    return {
      currentOrder,
      newOrder,
      sortedRows: this.sortRowsByColumn(rows, columnIndex, newOrder, hasGrandTotal),
    };
  }

  private getNextSortOrder(currentOrder: SortOrder): SortOrder {
    return currentOrder === "ASC" ? "DESC" : "ASC";
  }

  private sortRowsByColumn(
    rows: TableRow[],
    columnIndex: number,
    order: SortOrder,
    hasGrandTotal: boolean,
  ): TableRow[] {
    if ((rows?.length ?? 0) <= 1) {
      return [...rows];
    }

    if (!hasGrandTotal) {
      return [...rows].sort((leftRow: TableRow, rightRow: TableRow) =>
        this.compareRows(leftRow, rightRow, columnIndex, order),
      );
    }

    const rowsWithoutGrandTotal: TableRow[] = rows.slice(0, -1);
    const grandTotalRow: TableRow[] = rows.slice(-1);

    const sortedRows: TableRow[] = rowsWithoutGrandTotal.sort((leftRow: TableRow, rightRow: TableRow) =>
      this.compareRows(leftRow, rightRow, columnIndex, order),
    );

    return [...sortedRows, ...grandTotalRow];
  }

  private compareRows(leftRow: TableRow, rightRow: TableRow, columnIndex: number, order: SortOrder): number {
    let leftValue: unknown = this.getCellComparableValue(leftRow[columnIndex]);
    let rightValue: unknown = this.getCellComparableValue(rightRow[columnIndex]);

    if (order === "DESC") {
      const swapValue: unknown = rightValue;
      rightValue = leftValue;
      leftValue = swapValue;
    }

    if (this.isEmptyValue(leftValue)) {
      return -1;
    }

    if (this.isEmptyValue(rightValue)) {
      return 1;
    }

    if (leftValue === rightValue) {
      return 0;
    }

    return leftValue < rightValue ? -1 : 1;
  }

  private getCellComparableValue(cell: TableCell<unknown> | undefined): unknown {
    if (!cell) {
      return 0;
    }

    return cell.rawValue ?? cell.selected ?? cell.value ?? 0;
  }

  private isEmptyValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return true;
    }

    return EMPTY_VALS.includes(value as string);
  }
}
